import React, { useEffect, useState } from 'react';
import './index.scss';
import {
    Button,
    Card,
    Modal,
    Col,
    Divider,
    Image,
    Row,
    Table,
    Typography,
    notification,
    Space,
    Form,
    Input, Upload, Spin, Switch, Skeleton, Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
    UploadOutlined,
    ArrowLeftOutlined,
} from '@ant-design/icons';

import API_SERVICE from 'shared/services/api-service';
import { getImageUrl, UploadImage } from 'shared/services/Utility';

interface Props {
    location: any;
}

const { Title } = Typography;
const { TextArea } = Input;
export default function OrganizationForm(props: Props) {
    const { t } = useTranslation();
    const history: any = useHistory();
    const RouteParams: any = useParams();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [item, setItem] = useState(null as any);
    const [image, setImage] = useState({ image: null, imageId: null } as any);
    const [uploading, setUploading] = useState(false);
    const refreshItem = async () => {
        try {
            setFetching(true);
            const {
                data: { data },
            } = await API_SERVICE.fetchOrganizationById(RouteParams['id']);
            setItem({
                ...data,
                hasBBB: data.subscription === 'BBB',
                is_closed: !!data.is_closed,
                theme_config: data.theme_config ? JSON.parse(data.theme_config) : '',
                email: data.user.email,
            });
            setImage({ image: getImageUrl(data.logo), imageId: data.logo.id });
        } catch (e) {
            notification.error({
                message: t(API_SERVICE.handleErrors(e)),
            });
        } finally {
            setFetching(false);
        }
    };
    const uploadImage = async (file: any) => {
        setUploading(true);
        setImage({ image: null, imageId: null });
        const { image, imageId, error } = await UploadImage(file);
        if (error) {
            notification.error({ message: error });
        }
        setImage({ image, imageId });
        setUploading(false);
    };
    useEffect(() => {
        if (RouteParams['id']) {
            refreshItem();
        } else {
            setFetching(false);
        }
    }, []);

    if (fetching) {
        return <Skeleton active={true} />;
    }
    return (
        <div className={'list-page-wrapper'}>
            {
                // RouteParams['mode'] !== 'create'?
                <>
                    <Row gutter={20} justify={'space-between'}>
                        <Col flex={'auto'}>
                            <Space align={'start'}>
                                <Button type={'link'} onClick={() => history.goBack()} icon={<ArrowLeftOutlined />} /> <Title
                                level={3}>{RouteParams['mode'] === 'create' ? t('organization.create') : item.name}</Title>
                            </Space>
                        </Col>
                    </Row>
                    <Divider />
                    <Row>
                        <Col lg={12} md={12} sm={24} xs={24} className={'text-center'}>
                            {
                                uploading ? <Spin /> : (
                                    image.image ? <div>
                                        <Space direction={'vertical'}>
                                            {/*@ts-ignore*/}
                                            <Image src={image.image} width={'300px'} />
                                            {
                                                RouteParams['mode'] !== 'view' && <Button danger onClick={() => setImage({
                                                    image: null,
                                                    imageId: null,
                                                })}>{t('genDict.remove')}</Button>
                                            }
                                        </Space>
                                    </div> : <Upload beforeUpload={(file) => {
                                        if (!(file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg')) {
                                            notification.error({ message: `PNG/JPG/JPEG ${t('genDict.required')}` });
                                        }
                                        uploadImage(file);
                                        return file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg';
                                    }}>
                                        <Button icon={<UploadOutlined />}>{t('genDict.uploadLogo')}</Button>
                                    </Upload>
                                )
                            }
                        </Col>
                        <Col lg={12} md={12} sm={24} xs={24}>
                            <Form
                                size='large'
                                form={form}
                                initialValues={item}
                                fields={[]}
                                onFinish={(value) => {
                                    if (!image.imageId) {
                                        notification.error({ message: t('organization.image.required') });
                                        return;
                                    }
                                    const reqObj: any = value;
                                    reqObj['media_id'] = image.imageId;
                                    reqObj['theme_config'] = value.theme_config;
                                    reqObj['is_closed'] = reqObj['is_closed'] ? 1 : 0;
                                    reqObj['has_paypal'] = !reqObj['is_closed'] ? 1 : 0;
                                    reqObj['subscription'] = reqObj['hasBBB'] ? 'BBB' : 'CONTENT';
                                    setSaving(true);
                                    if (item && item.id) {
                                        API_SERVICE.updateOrganization(item.id, reqObj).then((d) => {
                                            history.goBack();
                                        }).catch((e) => {
                                            notification.error({ message: API_SERVICE.handleErrors(e) });
                                            setSaving(false);
                                        });
                                    } else {
                                        API_SERVICE.createOrganization(reqObj).then((d) => {
                                            history.goBack();
                                        }).catch((e) => {
                                            notification.error({ message: API_SERVICE.handleErrors(e) });
                                            setSaving(false);
                                        });
                                    }
                                }}
                                layout='vertical'
                                requiredMark={true}
                            >
                                <Form.Item
                                    label={t('organization.name.label')}
                                    name='name'
                                    rules={[
                                        { required: true, message: t('organization.name.required') },
                                    ]}
                                >
                                    <Input disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.email.label')}
                                    name='email'
                                    rules={[
                                        { required: true, type: 'email', message: t('organization.email.required') },
                                    ]}
                                >
                                    <Input disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.domain.label')}
                                    name='domain'
                                    rules={[
                                        { required: true, message: t('organization.domain.required') },
                                    ]}
                                >
                                    <Input disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.frontDomain.label')}
                                    name='front_domain'
                                    rules={[
                                        { required: true, message: t('organization.frontDomain.required') },
                                    ]}
                                >
                                    <Input disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                {RouteParams['mode'] === 'create' && <> <Form.Item
                                    label={t('organization.password.label')}
                                    name='password'
                                    rules={[
                                        {
                                            required: RouteParams['mode'] === 'create',
                                            message: t('organization.password.required'),
                                        },
                                        {
                                            min: RouteParams['mode'] === 'create' ? 6 : 0,
                                            message: t('organization.password.minLength6'),
                                        },
                                    ]}
                                >
                                    <Input

                                        onChange={() => {
                                            form.validateFields(['password_confirmation']);
                                        }}
                                        type='password'
                                    />
                                </Form.Item>
                                    <Form.Item
                                        label={t('organization.password_confirmation.label')}
                                        name='password_confirmation'
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (RouteParams['mode'] !== 'create') {
                                                        return Promise.resolve();
                                                    }
                                                    if (!value) {
                                                        return Promise.reject(
                                                            t('organization.password_confirmation.required'),
                                                        );
                                                    }
                                                    if (value && getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(
                                                        t('organization.password.unmatched'),
                                                    );
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input
                                            type='password'
                                        />
                                    </Form.Item>

                                </>
                                }
                                <Form.Item
                                    label={t('organization.isClosed.label')}
                                    name='is_closed'
                                    valuePropName={'checked'}
                                    rules={[]}
                                >
                                    <Switch disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.hasBBB.label')}
                                    name='hasBBB'
                                    valuePropName={'checked'}
                                    rules={[]}
                                >
                                    <Switch disabled={RouteParams['mode'] === 'view'} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.description.label')}
                                    name='description'
                                    rules={[
                                        { required: true, message: t('organization.description.required') },
                                    ]}
                                >
                                    <TextArea disabled={RouteParams['mode'] === 'view'} rows={3} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.theme.label')}
                                    name={['theme_config', 'color']}
                                    rules={[]}
                                    hidden={true}
                                >
                                    <Select>
                                        <Select.Option value={'default'}>{t('genDict.Default')}</Select.Option>
                                    <Select.Option value={'theme-red'}>
                                        <Space>{t('genDict.Red')} <div
                                        style={{ width: '20px', height: '20px', background: 'red' }}/></Space>
                                    </Select.Option>
                                    <Select.Option value={'theme-orange'}>
                                        <Space>{t('genDict.Orange')} <div
                                        style={{ width: '20px', height: '20px', background: 'orange' }}/></Space>
                                    </Select.Option>
                                    <Select.Option value={'theme-green'}>
                                        <Space>{t('genDict.Green')} <div
                                        style={{ width: '20px', height: '20px', background: 'green' }}/></Space>
                                    </Select.Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.homeTitle.label')}
                                    name={['theme_config', 'homeTitle']}
                                    rules={[]}
                                >
                                    <TextArea disabled={RouteParams['mode'] === 'view'} rows={3} />
                                </Form.Item>
                                <Form.Item
                                    label={t('organization.homeDescription.label')}
                                    name={['theme_config', 'homeDescription']}
                                    rules={[]}
                                >
                                    <TextArea disabled={RouteParams['mode'] === 'view'} rows={3} />
                                </Form.Item>
                                <br />
                                {
                                    RouteParams['mode'] !== 'view' && <Form.Item>
                                        <Button
                                            type='primary'
                                            htmlType='submit'
                                            loading={saving || uploading}
                                            shape='round'
                                            size={'large'}
                                            className='submit'
                                        >
                                            {t('genDict.save')}
                                        </Button>
                                    </Form.Item>
                                }
                            </Form>
                        </Col>
                    </Row>
                </>
                // :<></>
            }
            
        </div>
    );
}
